import React from 'react';
import {
  Badge, Box, HStack, Text, useColorModeValue,
} from '@chakra-ui/react';
import { InvoicePlan, InvoiceStep } from '../hooks/useInvoicePlan';
import { money } from '../utils/money';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';

// item 1446 (Chad: "let's be louder about what step we are processing — deposit, 50%, final
// 50%, etc.") — the loud, unmissable "THIS is the invoice you are about to create" block that
// heads every generate dialog. Says the step (Deposit / Final / Full / Re-bill), the percent,
// the dollars now vs the balance, and lists the earlier invoices on this work order — calling
// out any that is still a DRAFT. Informational only: it never disables the button.

const STEP_META: Record<InvoiceStep, { eyebrow: string; title: string; scheme: string }> = {
  deposit: { eyebrow: 'STEP 1 OF 2', title: 'DEPOSIT INVOICE', scheme: 'blue' },
  final: { eyebrow: 'STEP 2 OF 2', title: 'FINAL INVOICE', scheme: 'green' },
  full: { eyebrow: 'SINGLE INVOICE', title: 'FULL INVOICE', scheme: 'purple' },
  rebill: { eyebrow: 'ADDITIONAL INVOICE', title: 'RE-BILL', scheme: 'orange' },
  backorder: { eyebrow: 'PARTIAL · REMAINING LINES', title: 'BACKORDER INVOICE', scheme: 'teal' }, // item 1484
};

const pctLabel = (n: number): string => (Number.isInteger(n) ? `${n}%` : `${n.toFixed(2).replace(/\.?0+$/, '')}%`);

interface Props {
  plan?: InvoicePlan;
  isLoading?: boolean;
  // compact = inside a wizard that already has a card for the numbers
  compact?: boolean;
}

const InvoiceStepBanner: React.FC<Props> = ({ plan, isLoading, compact }) => {
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  if (isLoading || !plan) {
    return (
      <Box borderRadius="md" bg={bg} p={3}>
        <Text fontSize="sm" color={sub}>Working out which invoice this is…</Text>
      </Box>
    );
  }
  const meta = STEP_META[plan.step];
  const accent = `${meta.scheme}.400`;

  let line: string;
  if (plan.step === 'deposit') {
    const finalPct = plan.percent != null ? 100 - plan.percent : null;
    line = `${money(plan.billsNow)} of ${money(plan.total)} billed now · ${money(plan.balanceAfter)}${finalPct != null ? ` (${pctLabel(finalPct)})` : ''} balance goes on the FINAL invoice later`;
  } else if (plan.step === 'final') {
    line = `${money(plan.billsNow)} of ${money(plan.total)} — the balance after ${money(plan.billedSoFar)} already billed on the deposit`;
  } else if (plan.step === 'backorder') {
    line = `${money(plan.billsNow)} — only the line quantities not yet invoiced (backorder). ${money(plan.billedSoFar)} of ${money(plan.total)} is already on earlier invoices; the order closes once every line is billed.`;
  } else if (plan.step === 'full') {
    line = `${money(plan.billsNow)} — the whole job, paid in full. No deposit / final split, no second invoice.`;
  } else {
    line = `${money(plan.billsNow)} — this work order is already fully billed (${money(plan.billedSoFar)}); this is a fresh full bill (redo / correction).`;
  }

  return (
    <Box borderRadius="md" bg={bg} borderLeft="4px solid" borderLeftColor={accent} p={compact ? 3 : 4}>
      <HStack spacing={2} mb={1} flexWrap="wrap">
        <Badge colorScheme={meta.scheme} variant="solid" fontSize="0.6rem" letterSpacing="0.08em">{meta.eyebrow}</Badge>
        <Text fontWeight="bold" fontSize={compact ? 'md' : 'lg'} letterSpacing="0.02em">
          {meta.title}
          {plan.percent != null && plan.step !== 'rebill' ? ` · ${pctLabel(plan.percent)}` : ''}
        </Text>
      </HStack>
      <Text fontSize="sm">{line}</Text>
      {plan.prior.length > 0 && (
        <Box mt={2}>
          <Text fontSize="xs" color={sub} mb={1}>Already on this work order:</Text>
          {plan.prior.map((p) => (
            <HStack key={p.id} spacing={2} fontSize="xs" flexWrap="wrap">
              <Text fontWeight="semibold" whiteSpace="nowrap">{p.invoiceNumber ?? '(no number)'}</Text>
              <Text color={sub} noOfLines={1} title={p.serviceName ?? ''}>{p.serviceName ?? ''}</Text>
              <Text whiteSpace="nowrap">{money(p.billed)}</Text>
              {p.isFinalized
                ? <Badge colorScheme="green" variant="subtle" fontSize="0.55rem">FINALIZED</Badge>
                : <Badge colorScheme="orange" variant="solid" fontSize="0.55rem">STILL A DRAFT</Badge>}
              {p.sentDate && <Text color={sub} whiteSpace="nowrap">{`sent ${SqlDateTimeToDisplay(p.sentDate)}`}</Text>}
            </HStack>
          ))}
          {plan.drafts.length > 0 && (
            <Text fontSize="xs" color="orange.300" fontWeight="medium" mt={1}>
              {plan.drafts.length === 1
                ? `${plan.drafts[0].invoiceNumber ?? 'The earlier invoice'} has not been finalized yet — finalize and send it too.`
                : `${plan.drafts.length} earlier invoices have not been finalized yet — finalize and send them too.`}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

InvoiceStepBanner.defaultProps = { plan: undefined, isLoading: false, compact: false };

export default InvoiceStepBanner;
